import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { coach } from '../../lib/api';
import { useTranslation } from 'react-i18next';
import { Plus, Calendar, Users, Edit2, Trash2, CheckCircle } from 'lucide-react';

const CoachPlansPage = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [plans, setPlans] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [newPlan, setNewPlan] = useState({
    athlete_id: '',
    title: '',
    description: '',
    period_start: new Date().toISOString().split('T')[0],
    period_end: '',
    goals: [],
    notes: '',
    weekly_schedule: [
      { day: 'monday', type: 'tennis', duration: 60 },
      { day: 'wednesday', type: 'tennis', duration: 60 },
      { day: 'friday', type: 'fitness', duration: 45 }
    ]
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [plansRes, athletesRes] = await Promise.all([
        coach.getPlans({}),
        coach.getAthletes()
      ]);
      setPlans(plansRes.data);
      setAthletes(athletesRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePlan = async (e) => {
    e.preventDefault();
    try {
      await coach.createPlan({
        ...newPlan,
        period_start: new Date(newPlan.period_start).toISOString(),
        period_end: new Date(newPlan.period_end).toISOString()
      });
      setShowModal(false);
      setNewPlan({
        athlete_id: '',
        title: '',
        description: '',
        period_start: new Date().toISOString().split('T')[0],
        period_end: '',
        goals: [],
        notes: '',
        weekly_schedule: [
          { day: 'monday', type: 'tennis', duration: 60 },
          { day: 'wednesday', type: 'tennis', duration: 60 },
          { day: 'friday', type: 'fitness', duration: 45 }
        ]
      });
      loadData();
    } catch (error) {
      console.error('Failed to create plan:', error);
    }
  };

  const addScheduleItem = () => {
    setNewPlan({
      ...newPlan,
      weekly_schedule: [...newPlan.weekly_schedule, { day: 'tuesday', type: 'tennis', duration: 60 }]
    });
  };

  const updateScheduleItem = (index, field, value) => {
    const updated = [...newPlan.weekly_schedule];
    updated[index][field] = value;
    setNewPlan({ ...newPlan, weekly_schedule: updated });
  };

  const removeScheduleItem = (index) => {
    setNewPlan({
      ...newPlan,
      weekly_schedule: newPlan.weekly_schedule.filter((_, i) => i !== index)
    });
  };

  const dayLabels = {
    monday: t('coach.days.monday'),
    tuesday: t('coach.days.tuesday'),
    wednesday: t('coach.days.wednesday'),
    thursday: t('coach.days.thursday'),
    friday: t('coach.days.friday'),
    saturday: t('coach.days.saturday'),
    sunday: t('coach.days.sunday'),
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-8 mb-20 lg:mb-0">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-light tracking-tight text-primary">{t('coach.plans')}</h1>
          <p className="text-text-muted mt-1">{t('coach.plansSubtitle')}</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-hover"
        >
          <Plus size={18} />
          {t('coach.createPlan')}
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center text-primary">
              <Calendar size={24} />
            </div>
            <div>
              <p className="text-sm text-text-muted">{t('coach.totalPlans')}</p>
              <p className="text-2xl font-medium">{plans.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center text-green-600">
              <Users size={24} />
            </div>
            <div>
              <p className="text-sm text-text-muted">{t('coach.athletes')}</p>
              <p className="text-2xl font-medium">{athletes.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600">
              <CheckCircle size={24} />
            </div>
            <div>
              <p className="text-sm text-text-muted">{t('coach.activePlans')}</p>
              <p className="text-2xl font-medium">{plans.filter(p => p.is_active).length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Plans List */}
      <div className="space-y-4">
        {plans.map((plan) => (
          <div key={plan.id} className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-lg font-medium">{plan.title}</h3>
                  {plan.is_active && (
                    <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded">
                      {t('coach.active')}
                    </span>
                  )}
                </div>
                <p className="text-text-muted mb-4">{plan.description || t('coach.noDescription')}</p>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <div>
                    <p className="text-xs text-text-muted">{t('coach.athlete')}</p>
                    <p className="font-medium">
                      {athletes.find(a => a.id === plan.athlete_id)?.name || 'Unknown'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-text-muted">{t('coach.period')}</p>
                    <p className="font-medium">
                      {new Date(plan.period_start).toLocaleDateString('ru-RU')} - {new Date(plan.period_end).toLocaleDateString('ru-RU')}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-text-muted">{t('coach.weeklyTrainings')}</p>
                    <p className="font-medium">{plan.weekly_schedule?.length || 0}</p>
                  </div>
                  <div>
                    <p className="text-xs text-text-muted">{t('coach.goalsCount')}</p>
                    <p className="font-medium">{plan.goals?.length || 0}</p>
                  </div>
                </div>

                {plan.weekly_schedule && plan.weekly_schedule.length > 0 && (
                  <div className="border-t border-gray-100 pt-4">
                    <p className="text-xs text-text-muted mb-2">{t('coach.weeklyScheduleLabel')}</p>
                    <div className="flex flex-wrap gap-2">
                      {plan.weekly_schedule.map((item, index) => (
                        <span key={`${item.day}-${item.type}-${index}`} className="px-3 py-1 bg-gray-100 rounded-full text-sm">
                          {dayLabels[item.day]}: {item.type} ({item.duration} {t('common.min')})
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled
                  title={t('admin.inDevelopment')}
                  className="p-2 text-text-muted rounded opacity-50 cursor-not-allowed"
                >
                  <Edit2 size={18} />
                </button>
                <button
                  type="button"
                  disabled
                  title={t('admin.inDevelopment')}
                  className="p-2 text-text-muted rounded opacity-50 cursor-not-allowed"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
          </div>
        ))}
        {plans.length === 0 && (
          <div className="text-center py-12">
            <Calendar className="mx-auto text-slate-300 mb-4" size={48} />
            <p className="text-text-muted">{t('coach.noPlans')}</p>
          </div>
        )}
      </div>

      {/* Create Plan Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl m-4 max-h-screen overflow-y-auto">
            <h3 className="text-lg font-medium mb-4">{t('coach.newPlan')}</h3>
            <form onSubmit={handleCreatePlan} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">{t('coach.athlete')}</label>
                <select
                  value={newPlan.athlete_id}
                  onChange={(e) => setNewPlan({...newPlan, athlete_id: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-200 rounded-md"
                  required
                >
                  <option value="">{t('coach.selectAthlete')}</option>
                  {athletes.map((athlete) => (
                    <option key={athlete.id} value={athlete.id}>
                      {athlete.name} ({athlete.email})
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">{t('coach.planName')}</label>
                <input
                  type="text"
                  value={newPlan.title}
                  onChange={(e) => setNewPlan({...newPlan, title: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-200 rounded-md"
                  placeholder={t('coach.planNamePlaceholder')}
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">{t('coach.description')}</label>
                <textarea
                  value={newPlan.description}
                  onChange={(e) => setNewPlan({...newPlan, description: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-200 rounded-md"
                  rows={2}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">{t('coach.periodStart')}</label>
                  <input
                    type="date"
                    value={newPlan.period_start}
                    onChange={(e) => setNewPlan({...newPlan, period_start: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-200 rounded-md"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">{t('coach.periodEnd')}</label>
                  <input
                    type="date"
                    value={newPlan.period_end}
                    onChange={(e) => setNewPlan({...newPlan, period_end: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-200 rounded-md"
                    required
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">{t('coach.goals')}</label>
                <div className="space-y-2">
                  {newPlan.goals.map((goal, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        value={goal}
                        onChange={(e) => {
                          const updated = [...newPlan.goals];
                          updated[index] = e.target.value;
                          setNewPlan({...newPlan, goals: updated});
                        }}
                        className="flex-1 px-3 py-2 border border-gray-200 rounded-md"
                        placeholder={t('coach.goalPlaceholder')}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const updated = newPlan.goals.filter((_, i) => i !== index);
                          setNewPlan({...newPlan, goals: updated});
                        }}
                        className="px-3 py-2 text-red-600 hover:bg-red-50 rounded"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => setNewPlan({...newPlan, goals: [...newPlan.goals, '']})}
                    className="text-sm text-primary hover:underline"
                  >
                    {t('coach.addGoal')}
                  </button>
                </div>
              </div>
              
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium">{t('coach.weeklySchedule')}</label>
                  <button
                    type="button"
                    onClick={addScheduleItem}
                    className="text-sm text-primary hover:underline"
                  >
                    {t('coach.addScheduleItem')}
                  </button>
                </div>
                <div className="space-y-2">
                  {newPlan.weekly_schedule.map((item, index) => (
                    <div key={index} className="flex gap-2 items-center">
                      <select
                        value={item.day}
                        onChange={(e) => updateScheduleItem(index, 'day', e.target.value)}
                        className="flex-1 px-2 py-2 border border-gray-200 rounded-md text-sm"
                      >
                        {Object.entries(dayLabels).map(([value, label]) => (
                          <option key={value} value={value}>{label}</option>
                        ))}
                      </select>
                      <select
                        value={item.type}
                        onChange={(e) => updateScheduleItem(index, 'type', e.target.value)}
                        className="flex-1 px-2 py-2 border border-gray-200 rounded-md text-sm"
                      >
                        <option value="tennis">{t('coach.trainingTypes.tennis')}</option>
                        <option value="fitness">{t('coach.trainingTypes.fitness')}</option>
                        <option value="recovery">{t('coach.trainingTypes.recovery')}</option>
                        <option value="match">{t('coach.trainingTypes.match')}</option>
                      </select>
                      <input
                        type="number"
                        value={item.duration}
                        onChange={(e) => updateScheduleItem(index, 'duration', parseInt(e.target.value))}
                        className="w-20 px-2 py-2 border border-gray-200 rounded-md text-sm text-center"
                        placeholder={t('coach.minutesPlaceholder')}
                        min="15"
                        step="15"
                      />
                      <button
                        type="button"
                        onClick={() => removeScheduleItem(index)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">{t('common.notes')}</label>
                <textarea
                  value={newPlan.notes}
                  onChange={(e) => setNewPlan({...newPlan, notes: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-200 rounded-md"
                  rows={2}
                />
              </div>
              
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-200 rounded-md hover:bg-gray-50"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-hover"
                >
                  {t('coach.createPlan')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CoachPlansPage;
